import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useHistory} from 'react-router-dom';
import {firstBy} from 'thenby';

import {useModalContext} from '../../contexts/Modal';
import {useUserDataContext} from '../../contexts/UserData';

import {SubmitAnatomyCase} from '../Modal';
import {Row} from '@bootcamp/web/src/components/Grid';
import {H4, H5, Body3} from '@bootcamp/web/src/components/Typography';
import {TextTooltip} from '@bootcamp/web/src/bootcamps/pages/CreateTest/components/shared';
import Switch from 'rc-switch';
import "rc-switch/assets/index.css";

import {useReloadBlockerContext} from '@bootcamp/web/src/contexts/ReloadBlocker';
import {useTestNavigatorContext} from '@bootcamp/web/src/contexts/TestNavigator';
import {useTimerContext} from '@bootcamp/web/src/contexts/TestTimer';

import {IconButton} from '@bootcamp/web/src/components/Branding/Buttons';
import {ToggleSidebar} from '@bootcamp/web/src/components/Branding/Buttons/iconButtons';

import {CircleHalfFill as Partial} from '@styled-icons/fluentui-system-filled/CircleHalfFill'
import {Dismiss} from '@styled-icons/fluentui-system-filled/Dismiss';
import {Bars4 as ViewList} from '@styled-icons/heroicons-solid/Bars4';
import {Squares2x2 as ViewGrid} from '@styled-icons/heroicons-solid/Squares2x2';
import {Shuffle} from '@styled-icons/ionicons-solid/Shuffle';
import {Settings} from '@styled-icons/ionicons-outline/Settings';
import {Timeline} from '@styled-icons/fluentui-system-filled/Timeline';
import {Checkmark} from '@styled-icons/fluentui-system-filled/Checkmark';
import {DismissCircle} from '@styled-icons/fluentui-system-filled/DismissCircle';
import {MagnifyingGlass as Search} from '@styled-icons/heroicons-outline/MagnifyingGlass';
import {Tag} from '@styled-icons/fluentui-system-filled/Tag';
import {ChevronUp} from '@styled-icons/feather/ChevronUp';
import MasteryIcon from '../../bootcamps/components/MasteryIcon';

import styled, {css} from 'styled-components';
import {getTitleConfig, getTags} from  '@bootcamp/shared/src/util';
import Storage from '@aws-amplify/storage';

import {updateQuestionProgressData} from '@bootcamp/web/src/helpers';
import {trackQuizProgressPerformance} from '@bootcamp/shared/src/requests';

import {aggregateByTopic, aggregateBySubject} from '@bootcamp/shared/src/util/scoring';

import theme from '@bootcamp/shared/src/styles/theme';
import ProgressBar from '@bootcamp/web/src/components/ProgressBar';

export const CloseButton = styled(IconButton)`
  height: 56px;
  width: 56px;
  padding: 16px;

  ${({theme}) => theme.mediaQueries.mobileL} {
    height: 40px;
    width: 40px;
    padding: 10px;
  }
`;

export const CloseIcon = styled(Dismiss).attrs(props => ({size: 20, color: 'white'}))`
  ${({theme}) => theme.mediaQueries.mobileL} {
    height: 20px;
    width: 20px;
  }
`;

const Container = styled.div`
  width: 100%;
  background: ${({background}) => background};
  position: relative;
`;

const Type = styled(H4)`
  color: ${({theme}) => theme.overlays.opacity.light._400};
  margin-bottom: ${({theme}) => theme.layouts.spacing.xs};
  font-weight: 400;
  line-height: 100%;
`;

const Title = styled(H4)`
  color: ${theme.colors.neutralsPalette.white};
  font-weight: 700;
  line-height: 100%;
  font-size: 18px;
`;

const ButtonsContainer = styled(Row)`
  height: auto;
  align-items: center;
  justify-content: space-between;
`;

const TitleContainer = styled.div`
  padding: 16px 0px 0px;
`;

export const FilterSelector = styled.select`
  cursor: pointer;
  font-family: 'proxima-nova';
  border: none;
  width: 100%;
  min-width: 100%;
  appearance: none;
  background: url(data:image/svg+xml;base64,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) no-repeat 95% 50%;
  background-size: 18px;
  background-position: calc(100% - 5px) center;
  background-repeat: no-repeat;
  background-color: ${({theme}) => theme.overlays.opacity.light._100};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 0;
  margin-top: 8px;
  outline-width: 0px;
  color: ${({theme, light}) => light ? theme.colors.neutralsPalette.white : theme.colors.neutralsPalette.extraDark};
  option {
    color: ${({theme}) => theme.colors.neutralsPalette.extraDark};
  }
`;
const IconWrapper = styled.div`
`;
const IncorrectIcon = styled(Dismiss)`
  width: 14px;
`;

const TopicFilterSelector = styled(FilterSelector)`
  color: ${({theme, light}) => light ? theme.colors.neutralsPalette.white : theme.colors.neutralsPalette.extraDark};
`;

const ProgressContainer = styled(Row)`
  height: auto;
  align-items: flex-start;
  justify-content: center;
`;

const ToggleBackground = styled.div`
  background: ${({theme}) => theme.overlays.opacity.light._100};
  border-radius: 8px;
  padding: 4px;
  height: 32px;
`;

const ToggleContainer = styled(Row)`
  position: relative;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  flex: 1;
`;

const ToggleActive = styled.div`
  background: ${({theme}) => theme.overlays.opacity.light._100};
  position: absolute;
  left: ${({value, numOptions}) => value ? `${(100 / numOptions) * value}%` : 0};
  top: 0;
  height: 100%;
  width: ${({numOptions}) => `${100 / numOptions}%`};
  transition: left .3s;
  border-radius: 8px;
  box-shadow: ${({theme}) => theme.elevation.shadow.low};
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: white;
  font-family: proxima-nova;
  font-weight: 400px;
  font-size: 14px;
`;

export const QbankFilterToggle = ({visibleFilter, setVisibleFilter, setNavigationFilter, category}) => {
  return (
    <ToggleBackground>
      <ToggleContainer>
        <FilterContainer onClick={() => {
          setVisibleFilter();
          setNavigationFilter({});
        }}>
          All
        </FilterContainer>
        <FilterContainer active={visibleFilter === 'status'} onClick={() => {
          setVisibleFilter('status');
          setNavigationFilter({});
        }}>
          Status
        </FilterContainer>
        {category && <FilterContainer active={visibleFilter === 'category'} onClick={() => {
          setVisibleFilter('category');
          setNavigationFilter({});
        }}>
          {category}
        </FilterContainer>}
        <ToggleActive onClick={() => {
          setVisibleFilter();
          setNavigationFilter({});
        }} numOptions={category ? 3 : 2} value={[undefined, 'status', 'category'].indexOf(visibleFilter)} />
      </ToggleContainer>
    </ToggleBackground>
  )
}
export const CourseFilterToggle = ({navigationFilter, setNavigationFilter}) => {
  const {key, value} = navigationFilter;
  return (
    <ToggleBackground>
      <ToggleContainer>
        <FilterContainer active={key !== 'blockFilter' || !value} onClick={() => setNavigationFilter({})}>
          All
        </FilterContainer>
        <FilterContainer active={key === 'blockFilter' && value === 'videos'} onClick={() => setNavigationFilter({key: 'blockFilter', value: 'videos'})}>
          Videos
        </FilterContainer>
        <FilterContainer active={key === 'blockFilter' && value === 'quizzes'} onClick={() => setNavigationFilter({key: 'blockFilter', value: 'quizzes'})}>
          Quizzes
        </FilterContainer>
        <ToggleActive numOptions={3} value={key != 'blockFilter' || !value ? 0 : ['', 'videos', 'quizzes'].indexOf(value)} />
      </ToggleContainer>
    </ToggleBackground>
  )
}

export const GridToggle = ({value, setValue, themePalette}) => {
  return (
    <ToggleContainer onClick={() => setValue(!value)}>
      <IconContainer>
        <ListIcon active={!value} themePalette={themePalette} />
      </IconContainer>
      <IconContainer>
        <GridIcon active={value} themePalette={themePalette} />
      </IconContainer>
      <ToggleActive value={value} />
    </ToggleContainer>
  )
}

const GridIcon = styled(ViewGrid).attrs(props => ({size: 18}))`
  color: ${({themePalette, active}) => !active ? 'white' : themePalette.default};
  z-index: 2;
  transition: color .3s;
  width: 18px;
  height: 18px;
`;

const ListIcon = styled(ViewList).attrs(props => ({size: 18}))`
  color: ${({themePalette, active}) => !active ? 'white' : themePalette.default};
  z-index: 2;
  transition: color .3s;
  width: 18px;
  height: 18px;
`;

const SettingsToggle = styled(Settings)`
  color: white;
`;

const ProgressCount = styled(H5)`
  white-space: nowrap;
  width: auto;
  margin-top: 0;
  display: flex;
  align-items: center;
  line-height: normal;
  color: white;
  font-weight: 400;
  line-height: 100%;
  font-size: 14px;
`;

const Section = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${({theme}) => theme.overlays.opacity.dark._100};
`;

const TitleRow = styled(Row)`
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`;

const ProgressButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  border: 1px solid ${({theme}) => theme.darkModeEnabled ? theme.colors.darkModePalette.borderPrimary : theme.overlays.opacity.dark._50};
  background: ${({theme, toggled}) => toggled ? 'white' : theme.darkModeEnabled ? theme.colors.darkModePalette.surfaceSecondary : theme.overlays.opacity.light._100};
  transition: background .2s ease-in-out;
  border-radius: 8px;
  cursor: pointer;
  svg {
    width: 14px;
    height: 14px;
    color: ${({toggled, themePalette}) => toggled ? '#2F3037' : 'white'};
  }

  ${({toggled, theme}) => !toggled && css`
    &:hover {
      background: ${theme.darkModeEnabled ? theme.colors.darkModePalette.surfaceTertiary : 'rgba(255, 255, 255, 0.2)'}
    }
  `}
  margin-left: 4px;
  padding: 8px;
`;

const ShuffleIcon = styled(Shuffle).attrs(props => ({size: 18}))`
`;
const ProgressRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;
const SettingsArea = styled.div`
  width: 100%;
  max-height: ${({toggled}) => toggled ? '300px' : '0px'};
  overflow: hidden;
  transition: max-height .4s ease-in-out;
  border-bottom: ${({toggled}) => toggled ? `1px solid ${({theme}) => theme.overlays.opacity.dark._100}` : 'none'};
`;
const StyledTextTooltip = styled(TextTooltip).attrs(props => ({maxWidth: 244}))`
`;

const OptionBox = styled.div`
  box-sizing: border-box;
  padding: 8px 16px;
  width: 100%;
`;

const OptionTitle = styled(H4)`
  color: white;
  margin: 0;
  font-size: 14px;
  font-weight: 400;
`;

const StyledSwitch = styled(Switch)`
  margin-left: auto;
  &:after {
    transform: none !important;
    animation-name: none !important;
  }
`;

const Option = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: row;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: 8px 0px;
  &:first-child {
    padding-top: 0;
  }
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const OpenIndicator = styled(ChevronUp)`
  color: white;
  margin-left: auto;
  margin-right: 0;
  transition: .15s transform;
  transform: rotate(${({open}) => open ? '0deg' : '-180deg'});
`;

const FilterSection = styled.div`
  background: #FFFFFF1A;
  border-radius: 8px;
  padding: 0px 12px;
  margin-top: 8px;
`;

const FilterRow = styled.div`
  padding: 8px 0px;
  border-bottom: 1px solid #0000001A;
  &:first-child {
    padding-top: 12px;
  }
  &:last-child {
    padding-bottom: 12px;
    border-bottom: none;
  }
  display: flex;
  align-items: center;
  font-size: 14px;
  font-style: normal;
  font-weight: ${({isActive}) => isActive ? 700 : 400};
  line-height: 100%; /* 14px */
  color: white;
  cursor: pointer;
  font-family: proxima-nova;
  ${({isVisible, isActive}) => !isVisible && !isActive && css`display: none;`}
`;

const FilterText = styled.span`
  margin-left: 8px;
  margin-right: auto;
`;
const FilterCount = styled.span`
  margin-left: auto;
  margin-right: 0;
`
const MasteryTag = styled(MasteryIcon)`
  display: ${({level}) => !!level ? 'inline-block' : 'none'};
  max-width: 16px;
  height: 16px;
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SearchInput = styled.input`
  width: 100%;
  border: none;
  border-radius: 8px;
  padding: 8px;
  padding-left: 32px;
  background: ${({theme}) => theme.overlays.opacity.light._100};
  color: white;
  font-family: proxima-nova;
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;
  &::placeholder {
    color: ${({theme}) => theme.overlays.opacity.light._400};
  }
`;
const Top = ({
  title,
  parentTitle,
  setShowingSidebar,
  showingSidebar,
  visibleFilterState,
  progressBar,
  grid,
  setGrid,
  themePalette,
  classroom,
  showExitButton=true,
  hideQuestionList,
  hide=[],
  onSearch,
  hideSettings,
  openWithSettingsShowing,
  right
}) => {
  const [settingsToggled, setSettingsToggled] = useState(false);
  const [visibleFilter, setVisibleFilter] = visibleFilterState || [];
  const {modalDispatch} = useModalContext();
  const {toggleReloadBlocker} = useReloadBlockerContext();
  const {saveUserInteraction, bootcamp, DEFAULT_USER_ID, searchUserInteractions, toggleDarkMode, darkModeEnabled} = useUserDataContext();
  const hideAnswerData = searchUserInteractions('hideAnswerData') === true;
  const hideTimer = searchUserInteractions('hideTimer') === true;
  const splitScreenSolutions = searchUserInteractions('splitScreenSolutions') === true;
  const showKeyboardShortcuts = searchUserInteractions('showKeyboardShortcuts') === true;
  const prometricDelay = searchUserInteractions('prometricDelay') === true;
  const autoPlayEnabled = searchUserInteractions('autoPlayEnabled') === true;
  const autoTag = true;

  useEffect(() => {
    if (showingSidebar && openWithSettingsShowing) {
      setSettingsToggled(true);
    }
  }, [showingSidebar]);

  const {
    methods: {
      setNavigationFilter,
      transitionBlock,
      setCurrentIndex,
      toggleQuestionProgressShuffle,
      updateQuizProgress,
      autoTagOnSubmit
    },
    variables: {
      testBlockConnections,
      navigationFilter,
      blockIndex,
      template,
      type,
      test,
      shuffled,
      customTestConfig,
      quizProgress
    }
  } = useTestNavigatorContext();
  const {questions: questionProgresses} = quizProgress?.[blockIndex] || {};

  const {timeLeft, timeUp, questionStartTime} = useTimerContext();
  const {push, goBack, action} = useHistory();

  const canGoBack = action !== 'POP';
  const urlParts = window.location.pathname.split('/');
  const previousPath = urlParts
    .slice(0, 2)
    .join('/');

  const {questions: blockQuestionProgress} = {questions: [], ...quizProgress[blockIndex]};
  const questionIndex = blockQuestionProgress.findIndex(({current}) => current);

  const isFullLengthTestReview = template === 'testReview' && test?.config?.title.includes('Full Length');
  const isFullLengthOATTestReview = isFullLengthTestReview && test?.config?.title.includes('OAT');
  const [datTopics, datSubjects] = useMemo(() => {
    if (!['oat', 'dat'].includes(bootcamp) || template !== 'testReview' || !quizProgress) return [];
    return [aggregateByTopic(quizProgress), aggregateBySubject(quizProgress)]
  }, []);
  const questions = quizProgress.map(block => block.questions || []).flat();
  const {testSubjects, testTopics} = questions.reduce((acc, {question}) => {
    if (!question) return acc;
    const [subject, topic] = getTags(bootcamp, question.tags.items);
    // count the number of questions for each subject and topic within that subject
    acc.testSubjects[subject] = (acc.testSubjects[subject] || 0) + 1;
    acc.testTopics[subject] = acc.testTopics[subject] || {};
    acc.testTopics[subject][topic] = (acc.testTopics[subject][topic] || 0) + 1;

    return acc;
  }, {testSubjects: {}, testTopics: {}});

  const topicsBySubject = questions.reduce((acc, {question}) => {
    if (!question) return acc;
    const [subject, topic] = getTags(bootcamp, question.tags.items);
    acc[subject] = acc[subject] || [];
    if (!acc[subject].includes(topic)) acc[subject].push(topic);
    return acc;
  }, {});

  const activeSubjectFilter = Object.keys(testSubjects).length == 1 ? Object.keys(testSubjects)[0] : navigationFilter.key === 'subject' ? navigationFilter.value : navigationFilter.key === 'subjectTopic' ? navigationFilter.value.split('##')?.[0] : null;
  const activeTopics = Object.keys(testSubjects).length == 1 ? topicsBySubject[Object.keys(testSubjects)[0]] : navigationFilter.key === 'subject' ? topicsBySubject[navigationFilter.value] : navigationFilter.key === 'subjectTopic' ? topicsBySubject[navigationFilter.value.split('##')?.[0]] : [];

  // METHODS
  const handleGoBack = () => {
    if (['testReview'].includes(template) && ['dat', 'oat'].includes(bootcamp)) {
      const urlParams = new URLSearchParams(window.location.search);
      const testRoute = urlParams.get('testRoute');
      const testId = urlParams.get('id');
      const testProgressId = urlParams.get('testProgressId');
      const reviewRoute = `${testRoute}/review?id=${testId}&testProgressId=${encodeURIComponent(testProgressId)}&testRoute=${testRoute}`;
      testRoute && testId && testProgressId ? push(reviewRoute) : goBack();

    } else if (canGoBack && !['questionSearchBank'].includes(template)) {
      goBack();
    } else {
      push(previousPath);
    }
  };

  const questionCount = questions.length;

  const mastery = questions.reduce((acc, {masteryLevel}) => ({
    ...acc,
    ...((masteryLevel && masteryLevel !== 'none') ? {[masteryLevel]: acc[masteryLevel] + 1, total: acc.total + 1} : {}),
  }), {
    learning: 0,
    reviewing: 0,
    mastered: 0,
    total: 0
  });

  const completeLessons = quizProgress.reduce((acc, blockProgress) => blockProgress.status === 'complete' ? acc + 1 : acc, 0);
  const totalLessons = testBlockConnections.length - 2;

  const handleFilterChange = event => {
    if (parseInt(event.target.value)) {
      setNavigationFilter({});
      setVisibleFilter && setVisibleFilter();
      const blockTarget = parseInt(event.target.value);
      setCurrentIndex(blockTarget, 0);
      return;
    }

    const questionFilter = event.target.value;

    if (questionFilter) {

      const filterKey = questionFilter.match('topic') || questionFilter.match('subject')
        ? questionFilter.split('###')[0]
        : questionFilter === 'bookmarked'
        ? 'bookmarked'
        : (questionFilter === 'correct' || questionFilter === 'incorrect')
          ? 'didSelectCorrectAnswer'
          : ['fullCredit', 'partialCredit', 'noCredit'].includes(questionFilter)
            ? questionFilter
            : 'masteryLevel';

      const filterValue = questionFilter.match('topic') || questionFilter.match('subject')
        ? questionFilter.split('###')[1]
        : filterKey === 'bookmarked'
        ? true
        : filterKey === 'masteryLevel'
          ? questionFilter
          : ['fullCredit', 'partialCredit', 'noCredit'].includes(questionFilter)
            ? true
            : questionFilter === 'correct';
      if (filterKey === navigationFilter.key && filterValue === navigationFilter.value) {
        return setNavigationFilter({});
      }
      setNavigationFilter({ key: filterKey, value: filterValue });
    } else {
      setNavigationFilter({});
    }
  };

  const handleSuspendClick = async () => {
    const testStorageKey = customTestConfig?.config?.testStorageKey;

    try {
      // update custom test config to indicate suspension
      const updatedConfig = {
        ...customTestConfig,
        suspended: true,
        timeLeft: timeLeft?.length <= 5 ? `00:${timeLeft}` : timeLeft,
        questionIndex
      };

      await Storage.put(testStorageKey, JSON.stringify(updatedConfig), { contentType: 'application/json' });

      if (template === 'customTest' && !customTestConfig.submitted && !questionProgresses?.[questionIndex]?.didCheck) {
        await updateQuestionProgressData(updateQuizProgress, questionStartTime, questionProgresses, questionIndex);
      }

      toggleReloadBlocker(false);
      setTimeout(() => push(`/med-school/previous-tests`), 100);
    } catch (err) {
      console.log('error suspending test', err);
    }
  };

  const handleExitClick = async () => {
    const testStorageKey = customTestConfig?.config?.testStorageKey;

    try {
      // update custom test config to indicate suspension
      const updatedConfig = {
        ...customTestConfig,
        suspended: false,
        timeLeft: null,
        submitted: true,
      };

      await Storage.put(testStorageKey, JSON.stringify(updatedConfig), {contentType: 'application/json' });

    } catch (error) {
      console.log('error marking test as submitted', error);
    }

    if (template === 'customTest' && !questionProgresses?.[questionIndex]?.didCheck) {
      await updateQuestionProgressData(updateQuizProgress, questionStartTime, questionProgresses, questionIndex);
    }

    if (customTestConfig?.config && !customTestConfig.submitted) {
      if (!customTestConfig?.config?.tutorMode) {
        await trackQuizProgressPerformance(DEFAULT_USER_ID, quizProgress, `${DEFAULT_USER_ID}#${bootcamp}_Performance`);
      }

      await autoTagOnSubmit();
    }

    toggleReloadBlocker(false);
    setTimeout(() => push(`/${bootcamp}/previous-tests/review?key=${encodeURIComponent(customTestConfig?.config?.testStorageKey)}`), 100);
  };

  // USE EFFECTS
  useEffect(() => {
    // console.log('working here?', navigationFilter, blockIndex);
    if (!navigationFilter || !['masteryLevel', 'fullCredit', 'partialCredit', 'noCredit', 'topic', 'didSelectCorrectAnswer'].includes(navigationFilter.key) || blockIndex === 0) return;
    navigationFilter.value === 'all' ? transitionBlock('firstQuestionSet') : transitionBlock('firstFilterActive');
  }, [navigationFilter.key, navigationFilter.value]);

  useEffect(() => {
    if (timeUp && showExitButton) {
      modalDispatch({
        type: 'open',
        component: SubmitAnatomyCase,
        enableClickClose: true,
        componentProps: {
          headerText: 'No Time Remaining',
          bodyText: 'You have no more time remaining. You can review your test results from the previous tests page.',
          cancelConfig: {
            text: ''
          },
          confirmConfig: {
            text: 'View Test Results',
            colorConfig: themePalette,
            onConfirm: handleExitClick,
            shouldRedirect: false,
          }
        },
      })
    };
  }, [timeUp]);

  const titleConfig = getTitleConfig(title, classroom, template, customTestConfig);

  const exitProps = template === 'quickReview' ?
    {
      headerText: `Nice work! You’ve reached the end of your review.`,
      bodyText: `Would you like to keep reviewing or complete your review?`,
      cancelConfig: {
        text: 'Keep Reviewing',
        onCancel: () => {
          modalDispatch({type: 'close'});
        },
        shouldRedirect: false,

      },
      confirmConfig: {
        text: 'Complete Review',
        colorConfig: themePalette,
        onConfirm: () => {
          toggleReloadBlocker(false);
          setTimeout(() => handleGoBack(), 100);
        },
        shouldRedirect: false,
      }

    }
  : template === 'customTest' && !customTestConfig?.submitted ? {
      headerText: 'End Test',
      bodyText: 'Do you want to end this test? Once ended you can review your test results from the previous tests page.',
      cancelConfig: {
        text: 'Pause Test',
        onCancel: handleSuspendClick,
        shouldRedirect: false,
      },
      confirmConfig: {
        text: 'End Test',
        colorConfig: themePalette,
        onConfirm: handleExitClick,
        shouldRedirect: false,
      }
    } : {
    headerText: 'Would you like to end your review?',
    bodyText: 'You’ll be able to resume your review whenever you’re ready. Your tagged questions are automatically saved.',
    cancelConfig: {
      text: 'Return to Review',
      onCancel: () => {
        modalDispatch({type: 'close'});
      },
      shouldRedirect: false,

    },
    confirmConfig: {
      text: 'End Review',
      colorConfig: themePalette,
      onConfirm: () => {
        toggleReloadBlocker(false);
        setTimeout(() => handleGoBack(), 100);
      },
      shouldRedirect: false,
    }
  };
  const isUntutoredAndUnsubmitted = !!customTestConfig && !customTestConfig?.config?.tutorMode && !customTestConfig?.submitted;
  const {correct, incorrect, bookmarked, fullCredit, partialCredit, noCredit} = quizProgress.reduce((acc, blockProgress) => blockProgress?.questions ? blockProgress.questions.reduce((acc, question) => isUntutoredAndUnsubmitted ? acc : ({
    ...acc,
    correct: acc.correct + (question.didSelectCorrectAnswer && (question.didCheck || template == 'testReview' || type === 'readinessExam' || customTestConfig?.submitted) ? 1 : 0),
    incorrect: acc.incorrect + (!question.didSelectCorrectAnswer && (question.didCheck || template == 'testReview' || type === 'readinessExam' || customTestConfig?.submitted) ? 1 : 0),
    bookmarked: acc.bookmarked + (question.bookmarked ? 1 : 0),
    fullCredit: acc.fullCredit + (question.fullCredit && (question.didCheck || template == 'testReview' || type === 'readinessExam' || customTestConfig?.submitted) ? 1 : 0),
    partialCredit: acc.partialCredit + (question.partialCredit && (question.didCheck || template == 'testReview' || type === 'readinessExam' || customTestConfig?.submitted) ? 1 : 0),
    noCredit: acc.noCredit + (question.noCredit ? 1 : 0),
  }), acc) : acc, {
    correct: 0,
    incorrect: 0,
    bookmarked: 0,
    fullCredit: 0,
    partialCredit: 0,
    noCredit: 0,
  });

  const FilterRowOptions = [
    bootcamp === 'nclex' ? [
      {label: 'Full Credit', value: fullCredit, filterKey: 'fullCredit', filterValue: true, onClick: () => handleFilterChange({target: {value: 'fullCredit'}}), iconComponent: <Checkmark color={'white'} size={14} />},
      {label: 'Partial Credit', value: partialCredit, filterKey: 'partialCredit', filterValue: true, onClick: () => handleFilterChange({target: {value: 'partialCredit'}}), iconComponent: <Partial color={'white'} size={14} />},
      {label: 'No Credit', value: noCredit, filterKey: 'noCredit', filterValue: true, onClick: () => handleFilterChange({target: {value: 'noCredit'}}), iconComponent: <IconWrapper><IncorrectIcon color={'white'} size={11} /></IconWrapper>},
    ] : template !== 'questionSearchBank' ? [
      {label: 'Correct', value: correct, filterKey: 'didSelectCorrectAnswer', filterValue: true, onClick: () => handleFilterChange({target: {value: 'correct'}}), iconComponent: <Checkmark color={'white'} size={14} />},
      {label: 'Incorrect', value: incorrect, filterKey: 'didSelectCorrectAnswer', filterValue: false, onClick: () => handleFilterChange({target: {value: 'incorrect'}}), iconComponent: <IconWrapper><IncorrectIcon color={'white'} size={11} /></IconWrapper>},
    ] : [],
    [
      {label: 'Unused', value: questionCount - mastery.total, filterKey: 'masteryLevel', filterValue: 'none', onClick: () => handleFilterChange({target: {value: 'none'}}), iconComponent: <MasteryTag level={'none'} squared />},
      {label: 'Learning', value: mastery.learning, filterKey: 'masteryLevel', filterValue: 'learning', onClick: () => handleFilterChange({target: {value: 'learning'}}), iconComponent: <MasteryTag level={'learning'} squared />},
      {label: 'Reviewing', value: mastery.reviewing, filterKey: 'masteryLevel', filterValue: 'reviewing', onClick: () => handleFilterChange({target: {value: 'reviewing'}}), iconComponent: <MasteryTag level={'reviewing'} squared />},
      {label: 'Mastered', value: mastery.mastered, filterKey: 'masteryLevel', filterValue: 'mastered', onClick: () => handleFilterChange({target: {value: 'mastered'}}), iconComponent: <MasteryTag level={'mastered'} squared />},
      {label: 'Bookmarked', value: bookmarked, filterKey: 'bookmarked', filterValue: true, onClick: () => handleFilterChange({target: {value: 'bookmarked'}}), iconComponent: <MasteryTag level={'marked'} squared />},
    ]
  ]
  const FilterOptionComponent = ({label, value, filterKey, filterValue, onClick, iconComponent}) => (!navigationFilter.key || navigationFilter.key === filterKey && navigationFilter.value === filterValue) ? (
    <FilterRow key={label} isVisible={!navigationFilter.key} isActive={navigationFilter.key === filterKey && navigationFilter.value === filterValue} onClick={onClick}>
      {iconComponent}
      <FilterText>{label}</FilterText>
      <FilterCount>{value}</FilterCount>
    </FilterRow>
  ) : null
  const ScoreFilterRowComponents = FilterRowOptions[0].map(FilterOptionComponent).filter(Boolean);
  const MasteryFilterRowComponents = FilterRowOptions[1].map(FilterOptionComponent).filter(Boolean);

  const progressCount = questions.filter(question => question.selectedAnswerIndex > -1 || question.answerState?.answerState).length;
  const trackingProgressOnly = (customTestConfig && !customTestConfig?.config?.tutorMode && !customTestConfig?.submitted)
  const progressProps = {
    showLabel: false,
    withMargin: false,
    total: template === 'coursePlayer' ? totalLessons : questionCount,
    segments: template === 'coursePlayer' ? [{
      count: completeLessons
    }] : trackingProgressOnly ? [{
      count: progressCount,
      color: themePalette.light,
    }] : [
      {count: mastery.mastered},
      {count: mastery.reviewing},
      {count: mastery.learning},
    ]
  }
  const urlParams = new URLSearchParams(window.location.search);
  const searchQuery = urlParams.get('searchQuery')

  return (
    <Container onClick={() => !openWithSettingsShowing && setSettingsToggled(false)}>
      <Section style={{paddingTop: '16px'}}>
        <ButtonsContainer style={{justifyContent: 'space-between'}}>
          {showExitButton &&
            <CloseButton
              onClick={template === 'coursePlayer' ? handleGoBack : () => {
                modalDispatch({
                  type: 'open',
                  component: SubmitAnatomyCase,
                  enableClickClose: true,
                  componentProps: exitProps,
                })
              }}
              type={'secondary'}
              size={'large'}
              children={<CloseIcon />}
            />
          }
          <ToggleSidebar showingSidebar={showingSidebar} onClick={() => setShowingSidebar(!showingSidebar)} right={right} />
        </ButtonsContainer>
        <TitleContainer>
          <Type>{parentTitle || titleConfig.type}</Type>
          <Title>{titleConfig.title}</Title>
        </TitleContainer>
      </Section>
      {!hide?.includes('questions') && !hideSettings &&
        <Section>
          <TitleRow style={{marginBottom: '8px'}}>
            <Title style={{fontSize: '16px', fontWeight: '400'}}>{template === 'coursePlayer' ? 'Lessons Completed' : trackingProgressOnly ? 'Questions Answered' : 'Questions Tagged'}</Title>
          </TitleRow>
          <ProgressContainer>
            <ProgressRow>
              <ProgressCount>
                {template === 'coursePlayer' ? `${completeLessons}/${totalLessons}` : trackingProgressOnly ? `${progressCount}/${questionCount}` : `${mastery.total}/${questionCount}`}
              </ProgressCount>
              <ProgressBar {...progressProps} />
              {
                ['tbcQuestionBank', 'tbcSavedBank', 'masteryReview', 'bookmarkedQuestionReview', 'quickReview', 'customTest'].includes(template) && !['nclex'].includes(bootcamp) && !hide?.includes('questions') && (
                  <ProgressButtonWrapper
                    toggled={shuffled}
                    themePalette={themePalette}
                    onClick={() => {
                      toggleQuestionProgressShuffle();
                      setCurrentIndex(1, 0);
                    }}
                    children={<ShuffleIcon />}
                  />
                )
              }
              <ProgressButtonWrapper toggled={settingsToggled} themePalette={themePalette} onClick={event => {
                event.stopPropagation();
                setSettingsToggled(!settingsToggled);
              }}>
                <SettingsToggle />
              </ProgressButtonWrapper>
            </ProgressRow>
          </ProgressContainer>
        </Section>
      }
      {!hideSettings &&
        <SettingsArea toggled={hide?.includes('questions') || settingsToggled} onClick={e => e.stopPropagation()}>
          <OptionBox>
            {bootcamp !== 'chemistry' &&
              <Option>
                <OptionTitle as={StyledTextTooltip} id="answer-tooltip" text={"Show or hide answer stats"} hideIcon>
                  Answer Stats
                </OptionTitle>
                <StyledSwitch checked={!hideAnswerData} onChange={() => saveUserInteraction('hideAnswerData', !hideAnswerData)} />
              </Option>
            }
            {(!hide.includes('timer') && template !== 'testReview') &&
              <Option>
                <OptionTitle as={StyledTextTooltip} id="timer-tooltip" text={"Show or hide test timer"} hideIcon>
                  Timer
                </OptionTitle>
                <StyledSwitch checked={!hideTimer} onChange={() => saveUserInteraction('hideTimer', !hideTimer)} />
              </Option>
            }
            {!hide.includes('shortcuts') &&
              <Option>
                <OptionTitle as={StyledTextTooltip} id="keyboard-tooltip" text={"Enable or disable shortcuts"} hideIcon>
                  Show Keyboard Shortcuts
                </OptionTitle>
                <StyledSwitch checked={showKeyboardShortcuts} onChange={() => saveUserInteraction('showKeyboardShortcuts', !showKeyboardShortcuts)} />
              </Option>
            }
            {template != 'coursePlayer' && bootcamp === 'nclex' &&
              <Option>
                <OptionTitle as={StyledTextTooltip} id="restrictions-tooltip" text={"Adds delay between questions; disables cross-out and highlight to simulate Pearson testing experience"} hideIcon>
                  NCLEX Restrictions
                </OptionTitle>
                <StyledSwitch checked={prometricDelay} onChange={() => saveUserInteraction('prometricDelay', !prometricDelay)} />
              </Option>
            }
            {template != 'coursePlayer' &&
              <Option>
                <OptionTitle as={StyledTextTooltip} id="grid-tooltip" text={"View questions in grid"} hideIcon>
                  Grid View
                </OptionTitle>
                <StyledSwitch checked={grid} onChange={() => setGrid(!grid)} />
              </Option>
            }
            {template === 'coursePlayer' &&
              <Option>
                <OptionTitle as={StyledTextTooltip} id="auto-play-tooltip" text={"Automatically play the next video"} hideIcon>
                  Auto Play
                </OptionTitle>
                <StyledSwitch checked={autoPlayEnabled} onChange={() => saveUserInteraction('autoPlayEnabled', !autoPlayEnabled)} />
              </Option>
            }
            {!hide.includes('toggleDarkMode') &&
              <Option>
                <OptionTitle as={StyledTextTooltip} id="darkmode-tooltip" text={"Enable or disable Dark Mode"} hideIcon>
                  Dark Mode
                </OptionTitle>
                <StyledSwitch checked={darkModeEnabled} onChange={() => toggleDarkMode(!darkModeEnabled)} />
              </Option>
            }
            {!hide.includes('splitScreenSolutions') && !['mcat', 'nclex'].includes(bootcamp) &&
              <Option>
                <OptionTitle as={StyledTextTooltip} id="split-screen-solutions-tooltip" text={"Show explanations to the side"} hideIcon>
                  Split Screen Explanations
                </OptionTitle>
                <StyledSwitch checked={splitScreenSolutions} onChange={() => saveUserInteraction('splitScreenSolutions', !splitScreenSolutions)} />
              </Option>
            }
          </OptionBox>
        </SettingsArea>
      }

    {!hide?.includes('questions') &&
      <Section>
        {template === 'coursePlayer'
          ? <CourseFilterToggle navigationFilter={navigationFilter} setNavigationFilter={setNavigationFilter} />
          : <QbankFilterToggle
              visibleFilter={visibleFilter}
              setVisibleFilter={setVisibleFilter}
              setNavigationFilter={setNavigationFilter}
              category={
                ['tbcSavedBank', 'questionSearchBank'].includes(template)
                ? false
                : Object.keys(testSubjects).length > 1 && bootcamp === 'med-school'
                ? 'Systems'
                : Object.keys(testSubjects).length === 1 && bootcamp === 'med-school'
                ? 'Subjects'
                : Object.keys(testSubjects).length > 1
                ? 'Subjects'
                : 'Topics'}
              />
        }
        {!visibleFilter && navigationFilter.key != 'blockFilter' &&
          <div style={{ position: 'relative', marginTop: 8 }}>
            <label style={{ position: 'absolute', left: 8, top: 5, zIndex: 2, color: 'rgba(255,255,255,.5)' }} for="search" ><Search size={16} /></label>
            <SearchInput id="search" placeholder="Search..." value={navigationFilter.key === 'search' ? navigationFilter.value : searchQuery || ''} onChange={e => {
              const value = e.target.value;

              // if onSearch prop is passed, use that function to handle search
              if (typeof onSearch === 'function') {
                if (value.length >= 1) {
                  urlParams.set('searchQuery', value);
                } else {
                  urlParams.delete('searchQuery');
                }
                push({search: urlParams.toString()});
                onSearch(value);
                setNavigationFilter({ key: 'search', value });
                return;
              }

              if (value.length >= 1) {
                setNavigationFilter({ key: 'search', value });
                setVisibleFilter && setVisibleFilter();
              } else {
                setNavigationFilter({});
              }
            }} />
            {(navigationFilter.key == 'search' || searchQuery) &&
              <label
                style={{ position: 'absolute', right: 8, top: 5, zIndex: 2, color: 'rgba(255,255,255,.5)', cursor: 'pointer' }}
                for="search"
                onClick={() => {
                  if (urlParams.get('searchQuery')) {
                    urlParams.delete('searchQuery');
                    push({search: urlParams.toString()});
                  }
                  setNavigationFilter({});
                  onSearch && onSearch('');
                }}>
                  <DismissCircle size={16} />
              </label>
            }
          </div>
        }
        {visibleFilter === 'status' && [
          ScoreFilterRowComponents.length > 0 && <FilterSection children={ScoreFilterRowComponents} />,
          MasteryFilterRowComponents.length > 0 && <FilterSection children={MasteryFilterRowComponents} />
        ]}
        {visibleFilter === 'category' && Object.keys(testSubjects).length > 1 &&
          <FilterSelector onChange={handleFilterChange} light>
            <option value={''} selected={navigationFilter.value === 'all'}>All {bootcamp === 'med-school' ? 'Systems' : 'Subjects'}</option>
            {Object.entries(testSubjects).map(([subject, count]) => {
              const filterSubject = navigationFilter.key === 'subjectTopic' ? navigationFilter.value.split('##')[0] : navigationFilter.key === 'subject' ? navigationFilter.value : null;
              return (
                <option key={subject} value={`subject###${subject}`} selected={(navigationFilter.key === 'subject' || navigationFilter.key === 'subjectTopic') && filterSubject === subject}>{subject} ({count})</option>
              );
            })}
          </FilterSelector>
        }
        {(navigationFilter.key === 'subject' || navigationFilter.key === 'subjectTopic' || Object.keys(testSubjects).length === 1) && visibleFilter === 'category' &&
          <TopicFilterSelector onChange={handleFilterChange} light>
            <option value={`subject###${activeSubjectFilter}`} selected={navigationFilter?.value?.match('subject') && !navigationFilter?.value?.match('Topic')}>All {bootcamp === 'nclex' ? 'Systems' : bootcamp === 'med-school' ? 'Subjects' : 'Topics'}</option>
            {
              activeTopics
                ?.sort()
                ?.map(topic => {
                  const filterTopic = navigationFilter.key === 'subjectTopic' ? navigationFilter.value.split('##')[1] : null;
                  return (
                    <option key={topic} value={`subjectTopic###${activeSubjectFilter}##${topic}`} selected={['subjectTopic', 'topic'].includes(navigationFilter.key) && filterTopic === topic}>{topic} ({testTopics[activeSubjectFilter][topic]})</option>
                  );
                })
            }
          </TopicFilterSelector>
        }
      </Section>
      }
    </Container>
  );
}

Top.propTypes = {
  title: PropTypes.string
};
Top.defaultProps = {
  title: '',
};

export default Top;
